import type { OnboardingDraftV1 } from "@repo/types";
import { getBrowserPlatformApiClient } from "~/lib/trpc/browser-client";
import type { PlatformApiTrpcClient } from "~/lib/trpc/platform-api-adapter";
import { createBrowserPlatformApiTrpcAdapter } from "~/lib/trpc/platform-api-adapter";

export type OnboardingV1ClaimDraftResult = Awaited<
  ReturnType<PlatformApiTrpcClient["v2"]["onboarding"]["claimDraft"]["mutate"]>
>;

export type OnboardingV1ClaimedDraft = Extract<
  OnboardingV1ClaimDraftResult,
  { readonly status: "claimed" }
>["draft"];

export type OnboardingV1InstantCreateResult = Awaited<
  ReturnType<
    PlatformApiTrpcClient["v2"]["portfolio"]["create"]["instant"]["mutate"]
  >
>;

export interface OnboardingV1AccountDataSource {
  claimDraft(draft: OnboardingDraftV1): Promise<OnboardingV1ClaimDraftResult>;
  clearClaimedDraft(): Promise<void>;
  createInstantPortfolio(input: {
    readonly ownerAddress: string;
    readonly requestKey: string;
  }): Promise<OnboardingV1InstantCreateResult>;
  loadPortfolioPrimaryChainId(input: {
    readonly ownerAddress: string;
    readonly portfolioId: string;
  }): Promise<number | null>;
  loadClaimedDraft(): Promise<OnboardingV1ClaimedDraft | null>;
  markOnboardingCompleted(ownerAddress: string): Promise<void>;
  saveClaimedDraft(draft: OnboardingV1ClaimedDraft): Promise<void>;
}

export function hasOnboardingV1ProvisioningState(
  draft: Pick<
    OnboardingV1ClaimedDraft,
    | "createdPortfolioId"
    | "depositConfirmedAt"
    | "depositOperationId"
    | "depositTxHash"
    | "instantCreateRequestKey"
  >,
): boolean {
  return (
    draft.createdPortfolioId !== null ||
    draft.instantCreateRequestKey !== null ||
    draft.depositOperationId !== null ||
    draft.depositTxHash !== null ||
    draft.depositConfirmedAt !== null
  );
}

function parsePrimaryChainId(value: string | null | undefined): number | null {
  if (!value || !/^\d+$/u.test(value)) {
    return null;
  }
  const chainId = Number(value);
  return Number.isSafeInteger(chainId) && chainId > 0 ? chainId : null;
}

export function createOnboardingV1AccountDataSource(): OnboardingV1AccountDataSource {
  const platformApi = getBrowserPlatformApiClient();
  return {
    claimDraft: async (draft) =>
      await platformApi.v2.onboarding.claimDraft.mutate(draft),
    clearClaimedDraft: async () => {
      await platformApi.v2.portfolio.builder.saveDraft.mutate({ draft: null });
    },
    createInstantPortfolio: async ({ ownerAddress, requestKey }) => {
      const evmPlatformApi = createBrowserPlatformApiTrpcAdapter({
        getVmPreference: () => "evm",
        walletAddress: ownerAddress,
      });
      return await evmPlatformApi.v2.portfolio.create.instant.mutate({
        accountType: "ECDSA",
        creationMode: "onboarding-mirror",
        requestKey,
      });
    },
    loadPortfolioPrimaryChainId: async ({ ownerAddress, portfolioId }) => {
      const evmPlatformApi = createBrowserPlatformApiTrpcAdapter({
        getVmPreference: () => "evm",
        walletAddress: ownerAddress,
      });
      const result = await evmPlatformApi.v2.portfolio.get.query({
        archiveStatus: "all",
        portfolioId,
      });
      return parsePrimaryChainId(result.portfolio?.primary_chain_id);
    },
    loadClaimedDraft: async () =>
      (await platformApi.v2.portfolio.builder.getDraft.query()).draft,
    markOnboardingCompleted: async (ownerAddress) => {
      const evmPlatformApi = createBrowserPlatformApiTrpcAdapter({
        getVmPreference: () => "evm",
        walletAddress: ownerAddress,
      });
      await evmPlatformApi.userLegacy.markOnboardingCompleted.mutate();
    },
    saveClaimedDraft: async (draft) => {
      await platformApi.v2.portfolio.builder.saveDraft.mutate({ draft });
    },
  };
}
